import { AlertTriangle, CalendarClock, CheckCircle2, History, PauseCircle, Radar } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { useOperatorStatus } from "@/hooks/useOperatorStatus";
import type { OperatorSourceStatus, SignalAuditEvent } from "@/lib/api";

export default function OperatorStatusPage() {
  const statusQ = useOperatorStatus();
  const status = statusQ.data;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Operator Status</h1>
            <p className="text-xs text-muted-fg">
              Health summary for scraper workers, runtime state, and audit events.
            </p>
          </div>
          <Link
            to="/operator/scraper"
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium hover:bg-muted"
          >
            <Radar className="size-3.5" />
            Open scraper
          </Link>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {statusQ.isLoading && !status ? (
          <div className="py-10 text-center text-sm text-muted-fg">
            Loading operator status...
          </div>
        ) : statusQ.isError ? (
          <div className="py-10 text-center text-sm text-red-300">
            Failed to load operator status: {(statusQ.error as Error).message}
          </div>
        ) : status ? (
          <div className="space-y-6">
            <div className="grid gap-3 md:grid-cols-4">
              <StatusMetric
                icon={CalendarClock}
                label="Scheduled workers"
                value={status.active_schedules}
                tone={status.active_schedules > 0 ? "ok" : "warn"}
              />
              <StatusMetric
                icon={AlertTriangle}
                label="Recent failures"
                value={status.recent_failures}
                tone={status.recent_failures > 0 ? "error" : "ok"}
              />
              <StatusMetric
                icon={PauseCircle}
                label="Paused sources"
                value={status.paused_sources}
                tone={status.paused_sources > 0 ? "warn" : "ok"}
              />
              <StatusMetric
                icon={CheckCircle2}
                label="Scraper sources"
                value={status.scraper_sources}
                tone="neutral"
              />
            </div>

            <SourceHealthTable sourceStates={status.source_states} />
            <AuditFeed events={status.recent_audit_events} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatusMetric({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  tone: "ok" | "warn" | "error" | "neutral";
}) {
  return (
    <div className="rounded-md border border-border bg-bg/50 p-3">
      <div className="flex items-center gap-2 text-xs text-muted-fg">
        <Icon className={metricIconClass(tone)} />
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function SourceHealthTable({
  sourceStates,
}: {
  sourceStates: OperatorSourceStatus[];
}) {
  return (
    <section className="overflow-hidden rounded-md border border-border">
      <div className="grid grid-cols-[1.5fr_0.7fr_0.8fr_0.8fr_0.8fr_1fr] border-b border-border bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-fg">
        <span>Source</span>
        <span>Health</span>
        <span>Runtime</span>
        <span>Last run</span>
        <span>Next run</span>
        <span>Last audit</span>
      </div>
      {sourceStates.length === 0 ? (
        <div className="px-3 py-6 text-center text-sm text-muted-fg">
          No active scraper sources.
        </div>
      ) : (
        sourceStates.map((state) => (
          <SourceStatusRow key={state.source.id} state={state} />
        ))
      )}
    </section>
  );
}

function SourceStatusRow({ state }: { state: OperatorSourceStatus }) {
  return (
    <div className="grid grid-cols-[1.5fr_0.7fr_0.8fr_0.8fr_0.8fr_1fr] items-center border-b border-border/60 px-3 py-2 text-sm last:border-b-0">
      <div className="min-w-0">
        <Link
          to={`/operator/scraper?vendor=${encodeURIComponent(state.source.vendor)}`}
          className="truncate font-medium hover:text-accent hover:underline"
        >
          {state.source.display_name}
        </Link>
        <div className="truncate font-mono text-[11px] text-muted-fg">
          {state.source.vendor} / {state.source.id}
        </div>
      </div>
      <HealthBadge health={state.health} />
      <div className="min-w-0">
        <div className="truncate font-mono text-xs text-muted-fg">
          {state.runtime.mode}
        </div>
        <Badge variant={state.worker.scheduled ? "success" : "outline"}>
          {state.worker.scheduled ? "worker on" : "worker off"}
        </Badge>
      </div>
      <span className="text-xs text-muted-fg">{formatDate(state.last_run_at)}</span>
      <span className="text-xs text-muted-fg">{formatDate(state.next_run_at)}</span>
      <div className="min-w-0 text-xs text-muted-fg">
        {state.last_audit_event ? (
          <>
            <span className="text-fg">{state.last_audit_event.status}</span>
            <span> · {formatDate(state.last_audit_event.created_at)}</span>
          </>
        ) : (
          "none"
        )}
      </div>
    </div>
  );
}

function AuditFeed({ events }: { events: SignalAuditEvent[] }) {
  return (
    <section className="overflow-hidden rounded-md border border-border">
      <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2 text-sm font-medium">
        <History className="size-4" />
        Recent audit events
      </div>
      {events.length === 0 ? (
        <div className="px-3 py-6 text-center text-sm text-muted-fg">
          No audit events yet.
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {events.map((event) => (
            <AuditRow key={event.id} event={event} />
          ))}
        </div>
      )}
    </section>
  );
}

function AuditRow({ event }: { event: SignalAuditEvent }) {
  return (
    <div className="grid grid-cols-[0.7fr_1fr_1fr_0.8fr] gap-3 px-3 py-2 text-xs">
      <div className="flex items-center gap-2">
        <Badge variant={isSuccessStatus(event.status) ? "success" : "danger"}>
          {event.status}
        </Badge>
        <span className="text-muted-fg">{event.trigger_kind ?? "manual"}</span>
      </div>
      <div className="min-w-0">
        <div className="truncate font-medium">{event.event_type}</div>
        <div className="truncate font-mono text-[11px] text-muted-fg">
          {event.scraper_config_id ?? event.vendor_id ?? "unknown"}
        </div>
      </div>
      <div className="truncate text-muted-fg">
        seen {event.items_seen ?? 0} · saved {event.items_saved ?? 0} · dupes{" "}
        {event.duplicates_skipped ?? 0}
        {event.error_message ? ` · ${event.error_message}` : ""}
      </div>
      <div className="text-right text-muted-fg">{formatDate(event.created_at)}</div>
    </div>
  );
}

function HealthBadge({ health }: { health: OperatorSourceStatus["health"] }) {
  if (health === "error") return <Badge variant="danger">issue</Badge>;
  if (health === "warn") return <Badge variant="warn">watch</Badge>;
  if (health === "loading") return <Badge variant="outline">loading</Badge>;
  return <Badge variant="success">ok</Badge>;
}

function metricIconClass(tone: "ok" | "warn" | "error" | "neutral"): string {
  const base = "size-4";
  switch (tone) {
    case "ok":
      return `${base} text-emerald-300`;
    case "warn":
      return `${base} text-amber-300`;
    case "error":
      return `${base} text-red-300`;
    case "neutral":
      return `${base} text-muted-fg`;
  }
}

function isSuccessStatus(status: string): boolean {
  return ["started", "success", "succeeded", "skipped"].includes(status);
}

function formatDate(value: string | null): string {
  if (!value) return "never";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}
